import React, { useState, useEffect, useMemo, useRef } from 'react';
import type { PageItem, UploadPolicy } from '../../types';
import { ApiClient, type TemplatePointInfo } from '../../api/client';
import { useQuota } from '../../hooks/useQuota';
import { VariableInsertPicker, insertAtCursor, isLiveBlockItem, isSnippetItem } from '../common/VariableInsertPicker';
import { plainTextToHtml } from '../../utils/sanitizeHtml';
import { useAuth } from '../../context/AuthContext';
import {
  Sparkles,
  CheckCircle2,
  ArrowRight,
  ArrowLeft,
  Upload,
  X,
  RefreshCw,
  FileText,
  Trash2,
  Paperclip,
  AlertTriangle,
  HardDrive,
  ShieldAlert,
  FileSpreadsheet,
  Wand2,
  BookOpen,
  PlusCircle,
} from 'lucide-react';

const RichTextEditor = React.lazy(() => import('../common/RichTextEditor'));

/** a sablon főpontjainak formája: „1.3”, „2.10.”, „3.1” */
const PONT_RE = /^\d{1,2}(\.\d{1,2}){1,2}\.?$/;
const normPont = (p: string) => p.trim().replace(/\.+$/, '');

interface QuickWizardModalProps {
  isOpen: boolean;
  onClose: () => void;
  pages: PageItem[];
  onSuccess: (targetPageId: number, newRecordId?: number) => void;
}

// Alapértelmezett biztonsági tiltólista kiterjesztések
const DEFAULT_DENIED_EXT = new Set([
  'exe', 'com', 'bat', 'cmd', 'msi', 'msp', 'scr', 'pif', 'vbs', 'vbe', 'js', 'jse', 'wsf', 'wsh',
  'ps1', 'ps1xml', 'psc1', 'cpl', 'dll', 'sys', 'drv', 'gadget', 'application', 'lnk',
  'sh', 'bash', 'run', 'bin', 'out', 'elf', 'deb', 'rpm', 'appimage',
  'app', 'command', 'workflow', 'dmg', 'pkg',
  'jar', 'apk'
]);

function formatBytes(bytes: number): string {
  if (bytes <= 0) return '0 B';
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
}

function getFileIcon(filename: string) {
  const ext = (filename.split('.').pop() || '').toLowerCase();
  if (['xls', 'xlsx', 'csv', 'ods'].includes(ext)) {
    return <FileSpreadsheet className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />;
  }
  if (['pdf'].includes(ext)) {
    return <FileText className="w-4 h-4 text-rose-600 dark:text-rose-400" />;
  }
  return <Paperclip className="w-4 h-4 text-blue-600 dark:text-blue-400" />;
}

export const QuickWizardModal: React.FC<QuickWizardModalProps> = ({
  isOpen,
  onClose,
  pages,
  onSuccess,
}) => {
  const { tenant } = useAuth();
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [selectedPageId, setSelectedPageId] = useState<number | null>(null);
  const [title, setTitle] = useState('');
  const [pont, setPont] = useState('');
  const [content, setContent] = useState('');
  // sablonpont felismerése: 'plain' = szabad bejegyzés | 'template' = új, a sablonnal kitöltve | 'supplement' = meglévő kiegészítése
  const [tpl, setTpl] = useState<TemplatePointInfo | null>(null);
  const [tplLoading, setTplLoading] = useState(false);
  const [mode, setMode] = useState<'plain' | 'template' | 'supplement'>('plain');
  const [appliedPont, setAppliedPont] = useState('');
  const [target, setTarget] = useState<NonNullable<TemplatePointInfo['existing']>[number] | null>(null);
  const [kulsoLinkek, setKulsoLinkek] = useState('');
  const [frissites, setFrissites] = useState('');
  const [megorzes, setMegorzes] = useState('');
  const [contentMode, setContentMode] = useState<'plain' | 'rich'>('plain');
  const [contentHtml, setContentHtml] = useState('');
  const contentRef = useRef<HTMLTextAreaElement | null>(null);
  
  // Több fájl kezelése
  const [files, setFiles] = useState<File[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);

  // Tárhely és kvóta állapotok
  const [currentStorageBytes, setCurrentStorageBytes] = useState<number>(0);
  // a ténylegesen érvényes kvóta (saját vagy globális); a foglalt tárhelybe a Lomtár és az Archívum is beleszámít
  const { limitMb: quotaLimitMb, usedBytes: quotaUsedBytes } = useQuota(isOpen);
  const storageUsedNow = quotaUsedBytes ?? currentStorageBytes;
  const [uploadPolicy, setUploadPolicy] = useState<UploadPolicy | null>(null);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [internalPages, setInternalPages] = useState<PageItem[]>([]);
  const [isLoadingPages, setIsLoadingPages] = useState(false);

  // Modal megnyitásakor adatok betöltése
  useEffect(() => {
    if (!isOpen) return;

    // 1. Témakörök lekérése ha a prop üres
    if (!pages || pages.length === 0) {
      setIsLoadingPages(true);
      ApiClient.getPages()
        .then((res) => {
          if (res.ok && res.data && Array.isArray(res.data)) {
            setInternalPages(res.data);
          }
        })
        .finally(() => setIsLoadingPages(false));
    } else {
      setInternalPages(pages);
    }

    // 2. Jelenlegi tárhelyhasználat és feltöltési szabályzat lekérése
    Promise.all([
      ApiClient.getAttachmentsOverview(tenant?.id),
      ApiClient.getUploadPolicy()
    ])
      .then(([overviewRes, policyRes]) => {
        // B6: a /api/attachments/overview a méretet a `data` borítékban adja
        // ({ ok, data: { total_size } }) – a gyökérszintű olvasás mindig 0 B-t mutatott.
        const overview: any = (overviewRes as any).data ?? overviewRes;
        if (overviewRes.ok && overview?.total_size !== undefined) {
          setCurrentStorageBytes(Number(overview.total_size) || 0);
        }
        if (policyRes.ok && policyRes.data) {
          setUploadPolicy(policyRes.data);
        }
      });

  }, [isOpen, pages, tenant?.id]);

  // a beírt pontszám felismerése a 2026-os listában (kis késleltetéssel)
  useEffect(() => {
    if (!isOpen) return;
    const p = pont.trim();
    if (mode !== 'plain' && normPont(p) !== appliedPont) { setMode('plain'); setTarget(null); }
    if (!PONT_RE.test(p)) { setTpl(null); return; }
    let alive = true;
    setTplLoading(true);
    const t = setTimeout(() => {
      ApiClient.getTemplatePoint(p).then((r) => { if (alive) setTpl(r.ok && r.found ? r : null); }).finally(() => { if (alive) setTplLoading(false); });
    }, 350);
    return () => { alive = false; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pont, isOpen]);

  // Szabályzat szerinti tiltott kiterjesztések
  const effectiveDeniedExt = useMemo(() => {
    const set = new Set(DEFAULT_DENIED_EXT);
    // B9: a rendszerszintű tiltások is (a szerver is ezeket alkalmazza)
    const extraDenied = [uploadPolicy?.upload_denied_ext_extra, uploadPolicy?.upload_denied_ext_system].filter(Boolean).join(',');
    if (extraDenied) {
      extraDenied
        .split(',')
        .map((e) => e.trim().toLowerCase())
        .filter(Boolean)
        .forEach((e) => set.add(e));
    }
    return set;
  }, [uploadPolicy]);

  // Engedélyezett kiterjesztések listája (ha van megadva explicit allowlist)
  const allowedExtList = useMemo(() => {
    if (!uploadPolicy?.upload_allowed_ext) return [];
    return uploadPolicy.upload_allowed_ext
      .split(',')
      .map((e) => e.trim().toLowerCase())
      .filter(Boolean);
  }, [uploadPolicy]);

  if (!isOpen) return null;

  const effectivePages = internalPages.length > 0 ? internalPages : pages;
  const selectedPage = effectivePages.find((p) => p.id === selectedPageId);

  // Maximális tárhely (MB és Byte)
  const maxStorageMb = quotaLimitMb ?? tenant?.max_storage_mb ?? 500;
  const maxStorageBytes = maxStorageMb * 1024 * 1024;

  // Kiválasztott fájlok összesített mérete
  const totalNewFilesBytes = files.reduce((acc, f) => acc + (f.size || 0), 0);

  // Tervezett össztárhely a feltöltés után
  const projectedStorageBytes = storageUsedNow + totalNewFilesBytes;

  // Kvótatúllépés ellenőrzése
  const isOverQuota = maxStorageBytes > 0 && projectedStorageBytes > maxStorageBytes;

  const maxFileSizeMb = uploadPolicy?.upload_max_size_mb ? parseFloat(uploadPolicy.upload_max_size_mb) : null;

  // Fájlok hozzáadása és érvényesítése
  const handleAddFiles = (incomingList: FileList | File[]) => {
    setValidationError(null);
    const added: File[] = [];
    const errors: string[] = [];

    Array.from(incomingList).forEach((file) => {
      // 1. Üres fájl ellenőrzés
      if (!file.size || file.size <= 0) {
        errors.push(`„${file.name}” üres (0 byte)! 0 byte méretű fájl feltöltése nem engedélyezett.`);
        return;
      }

      // 2. Kiterjesztés ellenőrzése
      const ext = (file.name.split('.').pop() || '').toLowerCase();

      // 3. Tiltólista ellenőrzése
      if (effectiveDeniedExt.has(ext)) {
        errors.push(`„${file.name}” (.${ext}) biztonsági okból tiltólistán szerepel, feltöltése nem engedélyezett!`);
        return;
      }

      // 4. Fehérlista ellenőrzése (ha van)
      if (allowedExtList.length > 0 && !allowedExtList.includes(ext)) {
        errors.push(`„${file.name}” (.${ext}) nem engedélyezett! Engedélyezett kiterjesztések: ${allowedExtList.join(', ')}`);
        return;
      }

      // 5. Egyedi fájlméret ellenőrzése
      if (maxFileSizeMb && file.size > maxFileSizeMb * 1024 * 1024) {
        errors.push(`„${file.name}” mérete (${formatBytes(file.size)}) meghaladja a megengedett maximális ${maxFileSizeMb} MB méretet!`);
        return;
      }

      // 6. Duplikáció ellenőrzése
      if (files.some((f) => f.name === file.name && f.size === file.size)) {
        errors.push(`„${file.name}” már szerepel a feltöltendő fájlok listájában.`);
        return;
      }

      added.push(file);
    });

    if (errors.length > 0) {
      setValidationError(errors.join('\n'));
    }

    if (added.length > 0) {
      setFiles((prev) => [...prev, ...added]);
    }
  };

  const handleRemoveFile = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
    setValidationError(null);
  };

  const existingForPage = tpl?.existing?.find((e) => e.page_id === selectedPageId) || tpl?.existing?.[0] || null;
  const chapterPage = tpl?.chapter ? effectivePages.find((p) => p.slug === tpl.chapter!.slug) : undefined;

  const applyTemplate = () => {
    if (!tpl?.point) return;
    setTitle(tpl.point.cim);
    setContent(tpl.point.leiras);
    // tartalom-változóból formázott leírás: a formázott szerkesztőbe, a stílusokkal
    setContentHtml(tpl.point.leiras_html || '');
    setContentMode(tpl.point.leiras_html ? 'rich' : 'plain');
    setKulsoLinkek(tpl.point.kulso_linkek);
    setFrissites(tpl.point.frissites);
    setMegorzes(tpl.point.megorzesi_ido);
    if (chapterPage && chapterPage.id !== selectedPageId) setSelectedPageId(chapterPage.id);
    setAppliedPont(normPont(pont));
    setTarget(null);
    setMode('template');
  };
  const startSupplement = (ex: NonNullable<TemplatePointInfo['existing']>[number]) => {
    setTarget(ex);
    if (ex.page_id !== selectedPageId) setSelectedPageId(ex.page_id);
    setContent('');
    setContentHtml('');
    setContentMode(ex.has_html ? 'rich' : 'plain');
    setAppliedPont(normPont(pont));
    setMode('supplement');
  };
  const resetMode = () => { setMode('plain'); setTarget(null); setKulsoLinkek(''); setFrissites(''); setMegorzes(''); };

  const hasContent = contentMode === 'rich' ? contentHtml.replace(/<[^>]*>/g, '').trim() !== '' : content.trim() !== '';

  // meglévő bejegyzés kiegészítése: a szöveg a leírás után kerül, a fájlok a bejegyzéshez
  const finishSupplement = async () => {
    if (!target) return;
    const cur = await ApiClient.getRecord(target.id);
    const curRec: any = cur.ok ? (cur.data ?? cur) : null;
    if (!curRec) { alert('A kiegészítendő bejegyzés nem tölthető be: ' + (cur.error || '')); return; }
    let data: Record<string, any> = {};
    try { data = typeof curRec.data === 'string' ? JSON.parse(curRec.data) : (curRec.data || {}); } catch { data = {}; }
    if (hasContent) {
      if (data.leiras_html || contentMode === 'rich') {
        const add = contentMode === 'rich' ? contentHtml : plainTextToHtml(content.trim());
        data.leiras_html = `${data.leiras_html || plainTextToHtml(String(data.leiras || ''))}${add}`;
      } else {
        data.leiras = [String(data.leiras || '').trimEnd(), content.trim()].filter(Boolean).join('\n\n');
      }
    }
    const res = await ApiClient.saveRecord(target.page_id, data, target.id);
    if (!res.ok) { alert('Hiba a kiegészítés mentésekor: ' + (res.error || 'Ismeretlen hiba')); return; }
    if (files.length > 0) {
      const up = await ApiClient.uploadRecordAttachments(target.id, files).catch((e: any) => ({ ok: false, error: e?.message }));
      if (!up.ok) alert(`A kiegészítés elmentve, de a fájlok feltöltésekor hiba történt: ${(up as any).error || 'Ismeretlen hiba'}`);
    }
    onSuccess(target.page_id, target.id);
  };

  const resetAll = () => {
    setStep(1); setSelectedPageId(null); setTitle(''); setPont(''); setContent(''); setContentHtml(''); setContentMode('plain');
    setFiles([]); setValidationError(null); setTpl(null); resetMode();
  };

  const handleFinish = async () => {
    if (!selectedPageId) return;

    // Végső kvótaellenőrzés
    if (isOverQuota) {
      alert(`Kvótatúllépés! A fájlokkal a szervezet meghaladná a számára beállított ${maxStorageMb} MB tárhelykorlátot.`);
      return;
    }

    setIsSubmitting(true);

    if (mode === 'supplement') {
      await finishSupplement();
      setIsSubmitting(false);
      onClose();
      resetAll();
      return;
    }

    const recordData: Record<string, any> = {
      pont: pont.trim() || undefined,
      cim: title.trim(),
      megnevezes: title.trim(),
      ...(contentMode === 'rich' ? { leiras_html: contentHtml } : { leiras: content.trim(), ertek: content.trim() }),
      dokumentum_neve: title.trim(),
      ...(mode === 'template' ? {
        kulso_linkek: kulsoLinkek.trim(),
        ...(frissites.trim() ? { frissites: frissites.trim() } : {}),
        ...(megorzes.trim() ? { megorzesi_ido: megorzes.trim() } : {}),
        sablon_pont: normPont(pont),
        ...(tpl?.point?.snippet_ids?.length ? { tartalom_valtozok: tpl.point.snippet_ids } : {}),
      } : {}),
    };

    const res = await ApiClient.saveRecord(selectedPageId, recordData);
    if (res.ok) {
      const newRecordId = res.id || (res.data as any)?.id;
      // Csatolt fájlok feltöltése az új tételhez
      if (files.length > 0 && newRecordId) {
        try {
          const uploadRes = await ApiClient.uploadRecordAttachments(newRecordId, files);
          if (!uploadRes.ok) {
            alert(`A tétel létrejött, de a fájlok feltöltésekor hiba történt: ${uploadRes.error || 'Ismeretlen hiba'}`);
          }
        } catch (uploadErr: any) {
          alert(`A tétel létrejött, de hiba történt a fájlok feltöltésekor: ${uploadErr?.message || uploadErr}`);
        }
      }

      onSuccess(selectedPageId, newRecordId);
      onClose();
      resetAll();
    } else {
      alert('Hiba történt a közzétételkor: ' + (res.error || 'Ismeretlen hiba'));
    }
    setIsSubmitting(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-slate-950/60 dark:bg-black/75 backdrop-blur-xs animate-in fade-in overflow-y-auto">
      <div className="w-full max-w-xl rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 text-slate-900 dark:text-slate-100 p-5 sm:p-6 my-auto max-h-[92vh] flex flex-col space-y-4 sm:space-y-6 overflow-y-auto shadow-2xl transition-colors">
        {/* Fejléc */}
        <div className="flex items-center justify-between pb-4 border-b border-slate-100 dark:border-slate-800">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-50 dark:bg-emerald-500/20 text-emerald-600 dark:text-emerald-400 flex items-center justify-center font-bold">
              <Sparkles className="w-5 h-5" />
            </div>
            <div>
              <h3 className="font-bold text-base text-slate-900 dark:text-white">Egyszerűsített Közzétételi Varázsló</h3>
              <p className="text-xs text-slate-500 dark:text-slate-400">Gyors közzététel 3 egyszerű lépésben</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 text-slate-400 hover:text-slate-700 dark:hover:text-white rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition cursor-pointer"
            title="Bezárás"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Lépésjelző */}
        <div className="flex items-center justify-between px-2 sm:px-4">
          <div className={`flex items-center gap-2 text-xs font-semibold ${step >= 1 ? 'text-cyan-600 dark:text-cyan-400' : 'text-slate-400 dark:text-slate-600'}`}>
            <span className={`w-6 h-6 rounded-full border flex items-center justify-center text-xs ${
              step >= 1
                ? 'border-cyan-500 bg-cyan-50 dark:bg-cyan-950/50 text-cyan-700 dark:text-cyan-300'
                : 'border-slate-300 dark:border-slate-700 text-slate-400'
            }`}>
              1
            </span>
            <span className="hidden sm:inline">Téma kiválasztása</span>
            <span className="sm:hidden">Téma</span>
          </div>
          <div className="flex-1 mx-2 sm:mx-4 h-0.5 bg-slate-200 dark:bg-slate-800"></div>
          <div className={`flex items-center gap-2 text-xs font-semibold ${step >= 2 ? 'text-cyan-600 dark:text-cyan-400' : 'text-slate-400 dark:text-slate-600'}`}>
            <span className={`w-6 h-6 rounded-full border flex items-center justify-center text-xs ${
              step >= 2
                ? 'border-cyan-500 bg-cyan-50 dark:bg-cyan-950/50 text-cyan-700 dark:text-cyan-300'
                : 'border-slate-300 dark:border-slate-700 text-slate-400'
            }`}>
              2
            </span>
            <span className="hidden sm:inline">Adatok & Fájlok</span>
            <span className="sm:hidden">Fájlok</span>
          </div>
          <div className="flex-1 mx-2 sm:mx-4 h-0.5 bg-slate-200 dark:bg-slate-800"></div>
          <div className={`flex items-center gap-2 text-xs font-semibold ${step >= 3 ? 'text-cyan-600 dark:text-cyan-400' : 'text-slate-400 dark:text-slate-600'}`}>
            <span className={`w-6 h-6 rounded-full border flex items-center justify-center text-xs ${
              step >= 3
                ? 'border-cyan-500 bg-cyan-50 dark:bg-cyan-950/50 text-cyan-700 dark:text-cyan-300'
                : 'border-slate-300 dark:border-slate-700 text-slate-400'
            }`}>
              3
            </span>
            <span className="hidden sm:inline">Jóváhagyás</span>
            <span className="sm:hidden">Kész</span>
          </div>
        </div>

        {/* 1. Lépés: Oldal / Téma választó */}
        {step === 1 && (
          <div className="space-y-4">
            <div className="text-xs font-medium text-slate-700 dark:text-slate-300">
              Válaszd ki a közzétenni kívánt közérdekű adat témakörét:
            </div>

            {isLoadingPages ? (
              <div className="py-12 flex flex-col items-center justify-center gap-3 text-slate-500 dark:text-slate-400 text-xs">
                <RefreshCw className="w-6 h-6 animate-spin text-cyan-600 dark:text-cyan-400" />
                <span>Témakörök betöltése...</span>
              </div>
            ) : effectivePages.length === 0 ? (
              <div className="py-8 text-center bg-slate-50 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-800 rounded-xl p-4 text-xs text-slate-500 dark:text-slate-400 space-y-2">
                <FileText className="w-8 h-8 mx-auto text-slate-400 opacity-60" />
                <p>Jelenleg nincsenek elérhető témakörök.</p>
                <button
                  type="button"
                  onClick={() => {
                    setIsLoadingPages(true);
                    ApiClient.getPages()
                      .then((res) => {
                        if (res.ok && res.data) setInternalPages(res.data);
                      })
                      .finally(() => setIsLoadingPages(false));
                  }}
                  className="px-3 py-1.5 bg-slate-200 dark:bg-slate-700 hover:bg-slate-300 text-slate-800 dark:text-slate-200 rounded-lg text-xs font-semibold transition cursor-pointer"
                >
                  Újrapróbálkozás
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 max-h-72 overflow-y-auto pr-1">
                {effectivePages.map((p) => {
                  const isSelected = selectedPageId === p.id;
                  return (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => setSelectedPageId(p.id)}
                      className={`p-3 rounded-xl border text-left transition flex items-start gap-3 cursor-pointer ${
                        isSelected
                          ? 'bg-cyan-50 dark:bg-cyan-950/40 border-cyan-500 text-cyan-900 dark:text-cyan-100 shadow-sm ring-1 ring-cyan-500'
                          : 'bg-slate-50 dark:bg-slate-800/60 border-slate-200 dark:border-slate-700/80 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700/60 hover:border-slate-300 dark:hover:border-slate-600'
                      }`}
                    >
                      <span className="text-xl shrink-0 mt-0.5">{p.icon || '📄'}</span>
                      <div className="min-w-0 flex-1">
                        <div className="font-semibold text-xs text-slate-900 dark:text-white truncate">{p.title}</div>
                        <div className="text-[10px] text-slate-500 dark:text-slate-400 line-clamp-1 mt-0.5">
                          {p.description || (p as any).category || 'Közzétételi pont'}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}

            <div className="flex justify-end pt-3 border-t border-slate-100 dark:border-slate-800">
              <button
                disabled={!selectedPageId}
                onClick={() => setStep(2)}
                className="px-5 py-2 rounded-xl bg-cyan-600 hover:bg-cyan-500 disabled:opacity-40 text-white font-bold text-xs shadow-md shadow-cyan-600/20 flex items-center gap-2 transition cursor-pointer"
              >
                <span>Tovább az adatokhoz</span>
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {/* 2. Lépés: Adatok és Több Fájl feltöltése kvóta- és tiltásellenőrzéssel */}
        {step === 2 && (
          <div className="space-y-4">
            <div className="space-y-3 text-xs">
              <div>
                <label className="block text-slate-700 dark:text-slate-300 font-medium mb-1">
                  Törvényi pont sorszáma (opcionális)
                </label>
                <input
                  type="text"
                  placeholder="pl. 1.2 vagy 3.1"
                  value={pont}
                  onChange={(e) => setPont(e.target.value)}
                  aria-describedby="qw-tpl"
                  className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-700 rounded-xl px-3 py-2 text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-500/20 focus:border-cyan-500 transition"
                />
                <div id="qw-tpl" aria-live="polite">
                  {tplLoading && PONT_RE.test(pont.trim()) && !tpl && <p className="mt-1 text-[11px] text-slate-400 flex items-center gap-1"><RefreshCw className="w-3 h-3 animate-spin" /> Keresés a közzétételi listában…</p>}
                  {tpl?.found && tpl.point && mode === 'plain' && (
                    <div className="mt-2 p-3 rounded-xl border border-cyan-200 dark:border-cyan-800 bg-cyan-50/60 dark:bg-cyan-950/30 space-y-2">
                      <div className="flex items-start gap-2">
                        <BookOpen className="w-4 h-4 text-cyan-600 dark:text-cyan-400 shrink-0 mt-0.5" />
                        <div className="min-w-0">
                          <div className="font-semibold text-slate-900 dark:text-white">Közzétételi lista 2026 – {tpl.point.pont}. pont</div>
                          <div className="text-[11px] text-slate-600 dark:text-slate-300 leading-snug">{tpl.point.cim}</div>
                          {tpl.chapter && <div className="text-[10px] text-slate-500 mt-0.5">Fejezet: {tpl.chapter.pageTitle}</div>}
                        </div>
                      </div>
                      {existingForPage ? (
                        <div className="space-y-1.5">
                          <p className="text-[11px] text-slate-700 dark:text-slate-300">
                            Ez a pont már szerepel: <strong>„{existingForPage.cim || tpl.point.cim}”</strong>{existingForPage.year ? ` (${existingForPage.year})` : ''} – {existingForPage.page_title}. A most megadott szöveg és fájlok ehhez a bejegyzéshez kerülnek.
                          </p>
                          <div className="flex flex-wrap gap-1.5">
                            <button type="button" onClick={() => startSupplement(existingForPage)}
                              className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-cyan-600 hover:bg-cyan-500 text-white text-[11px] font-semibold cursor-pointer">
                              <PlusCircle className="w-3.5 h-3.5" /> Kiegészítem ezt a pontot
                            </button>
                            {(tpl.existing?.length || 0) > 1 && (
                              <select aria-label="Másik meglévő bejegyzés kiegészítése" className="text-[11px] rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-2 py-1 min-w-0 flex-1 max-w-full truncate"
                                value="" onChange={(e) => { const ex = tpl.existing!.find((x) => x.id === Number(e.target.value)); if (ex) startSupplement(ex); }}>
                                <option value="">Másik bejegyzés ({tpl.existing!.length})…</option>
                                {tpl.existing!.map((x) => <option key={x.id} value={x.id}>{x.cim || '(cím nélkül)'}{x.year ? ` – ${x.year}` : ''} · {x.page_title}</option>)}
                              </select>
                            )}
                          </div>
                        </div>
                      ) : (
                        <div className="space-y-1.5">
                          <p className="text-[11px] text-slate-700 dark:text-slate-300">
                            Ennél a szervezetnél még nincs ilyen bejegyzés. Betöltheted a sablon címét, leírását, külső linkjeit, frissítését és megőrzését a szervezet adataival kitöltve, majd kiegészítheted.
                          </p>
                          <button type="button" onClick={applyTemplate}
                            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-cyan-600 hover:bg-cyan-500 text-white text-[11px] font-semibold cursor-pointer">
                            <Wand2 className="w-3.5 h-3.5" /> Sablon betöltése ehhez a ponthoz
                          </button>
                        </div>
                      )}
                      {chapterPage && selectedPageId !== chapterPage.id && !existingForPage && (
                        <p className="text-[10px] text-amber-700 dark:text-amber-400">A sablon szerint ez a pont a(z) „{chapterPage.title}” oldalra tartozik – a betöltés oda állítja.</p>
                      )}
                    </div>
                  )}
                  {mode !== 'plain' && (
                    <div className="mt-2 p-2.5 rounded-xl border border-emerald-200 dark:border-emerald-800 bg-emerald-50/70 dark:bg-emerald-950/30 flex items-start justify-between gap-2 text-[11px]">
                      <span className="text-emerald-800 dark:text-emerald-300">
                        {mode === 'supplement'
                          ? <>Kiegészítés: <strong>„{target?.cim}”</strong> ({target?.page_title}) – a szöveg a meglévő leírás után kerül, a fájlok a bejegyzéshez.</>
                          : <>A sablon betöltve ({appliedPont}. pont) – a mezők szabadon szerkeszthetők.</>}
                      </span>
                      <button type="button" onClick={resetMode} className="text-slate-500 hover:text-slate-800 dark:hover:text-slate-200 underline cursor-pointer shrink-0">Mégse</button>
                    </div>
                  )}
                </div>
              </div>

              {mode !== 'supplement' && (
              <div>
                <label className="block text-slate-700 dark:text-slate-300 font-medium mb-1">
                  Megnevezés / Dokumentum címe <span className="text-rose-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  placeholder="pl. Szervezeti és Működési Szabályzat (SZMSZ)"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-700 rounded-xl px-3 py-2 text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-500/20 focus:border-cyan-500 transition"
                />
              </div>
              )}

              <div>
                <div className="flex items-center justify-between gap-2 mb-1 flex-wrap">
                  <label htmlFor="qw-content" className="block text-slate-700 dark:text-slate-300 font-medium">
                    {mode === 'supplement' ? 'Kiegészítés (a meglévő leírás után kerül)' : 'Szöveges tartalom vagy összefoglaló'}
                  </label>
                  <div className="flex items-center gap-1.5">
                    <div className="inline-flex rounded-md border border-slate-200 dark:border-slate-700 overflow-hidden text-[11px]" role="group" aria-label="Szöveg módja">
                      {(['plain', 'rich'] as const).map((m) => (
                        <button key={m} type="button" aria-pressed={contentMode === m}
                          onClick={() => {
                            if (m === contentMode) return;
                            if (m === 'rich') setContentHtml((h) => h || plainTextToHtml(content));
                            else if (contentHtml.replace(/<[^>]*>/g, '').trim() && !window.confirm('Egyszerű szövegre váltva a formázás elvész. Folytatod?')) return;
                            else setContent(contentHtml.replace(/<br\s*\/?>/gi, '\n').replace(/<\/p>/gi, '\n').replace(/<[^>]*>/g, '').trim());
                            setContentMode(m);
                          }}
                          className={`px-2 py-0.5 font-semibold cursor-pointer ${contentMode === m ? 'bg-slate-800 text-white dark:bg-slate-200 dark:text-slate-900' : 'text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800'}`}>
                          {m === 'plain' ? 'Egyszerű' : 'Formázott'}
                        </button>
                      ))}
                    </div>
                    {contentMode === 'plain' && (
                      <VariableInsertPicker
                        personnelBlocks
                        targetRef={contentRef}
                        onInsert={(text) => setContent((cur) => insertAtCursor(contentRef.current, cur, text))}
                      />
                    )}
                  </div>
                </div>
                {contentMode === 'plain' ? (
                  <textarea
                    id="qw-content"
                    ref={contentRef}
                    rows={mode === 'plain' ? 3 : 6}
                    placeholder={mode === 'supplement' ? 'Az új információ, amely a meglévő leírás után kerül…' : 'Rövid leírás vagy a közvetlenül közzéteendő információ...'}
                    value={content}
                    onChange={(e) => setContent(e.target.value)}
                    className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-700 rounded-xl px-3 py-2 text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-500/20 focus:border-cyan-500 transition resize-y"
                  />
                ) : (
                  <React.Suspense fallback={<div className="h-32 rounded-xl border border-slate-200 dark:border-slate-700 flex items-center justify-center text-xs text-slate-400"><RefreshCw className="w-4 h-4 animate-spin mr-2" /> Szerkesztő betöltése…</div>}>
                    <RichTextEditor
                      id="qw-content"
                      value={contentHtml}
                      onChange={(html) => setContentHtml(html)}
                      contentMaxHeight="16rem"
                      extraTools={(ed) => (
                        <VariableInsertPicker
                          targetRef={{ current: null }}
                          personnelBlocks
                          onInsert={(text, item) => {
                            const esc = text.replace(/[&<>]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c] as string));
                            // tartalom-változó: a teljes tartalom a stílusaival (formázott), vagy bekezdésekre bontott szöveg
                            if (isSnippetItem(item)) ed.chain().focus().insertContent(item.html || plainTextToHtml(text)).run();
                            else if (isLiveBlockItem(item)) ed.chain().focus().insertContent(`<p>${esc}</p>`).run();
                            else ed.chain().focus().insertContent(esc).run();
                          }}
                        />
                      )}
                    />
                  </React.Suspense>
                )}
              </div>

              {mode === 'template' && (
                <div className="space-y-3 p-3 rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-950/30">
                  <div>
                    <label htmlFor="qw-links" className="block text-slate-700 dark:text-slate-300 font-medium mb-1">Kapcsolódó jogszabályok és külső hivatkozások</label>
                    <textarea id="qw-links" rows={3} value={kulsoLinkek} onChange={(e) => setKulsoLinkek(e.target.value)}
                      placeholder="Soronként: Címke — URL" className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-700 rounded-xl px-3 py-2 text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-500/20 focus:border-cyan-500 transition resize-y font-mono text-[11px]" />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label htmlFor="qw-frissites" className="block text-slate-700 dark:text-slate-300 font-medium mb-1">Frissítés</label>
                      <input id="qw-frissites" value={frissites} onChange={(e) => setFrissites(e.target.value)} className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-700 rounded-xl px-3 py-2 text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-500/20 focus:border-cyan-500 transition" />
                    </div>
                    <div>
                      <label htmlFor="qw-megorzes" className="block text-slate-700 dark:text-slate-300 font-medium mb-1">Megőrzés</label>
                      <input id="qw-megorzes" value={megorzes} onChange={(e) => setMegorzes(e.target.value)} className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-700 rounded-xl px-3 py-2 text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-500/20 focus:border-cyan-500 transition" />
                    </div>
                  </div>
                </div>
              )}

              {/* Többfájlos feltöltő zóna */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-slate-700 dark:text-slate-300 font-medium">
                    Csatolt fájlok (több különböző típus is feltölthető: PDF, XLSX, DOCX, CSV, stb.)
                  </label>
                  <span className="text-[11px] font-semibold text-slate-500 dark:text-slate-400">
                    {files.length > 0 ? `${files.length} db fájl (${formatBytes(totalNewFilesBytes)})` : 'Nincs csatolmány'}
                  </span>
                </div>

                <div
                  onDragOver={(e) => {
                    e.preventDefault();
                    setIsDragging(true);
                  }}
                  onDragLeave={() => setIsDragging(false)}
                  onDrop={(e) => {
                    e.preventDefault();
                    setIsDragging(false);
                    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
                      handleAddFiles(e.dataTransfer.files);
                    }
                  }}
                  className={`border-2 border-dashed rounded-xl p-4 text-center transition ${
                    isDragging
                      ? 'border-cyan-500 bg-cyan-50/60 dark:bg-cyan-950/40 ring-2 ring-cyan-500/30'
                      : 'border-slate-300 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-950/60 hover:bg-slate-100/50 dark:hover:bg-slate-900/80'
                  }`}
                >
                  <input
                    type="file"
                    id="wizard-files"
                    multiple
                    onChange={(e) => {
                      if (e.target.files && e.target.files.length > 0) {
                        handleAddFiles(e.target.files);
                        // Reset input értéket, hogy ugyanazt a fájlt újra lehessen választani ha törölte
                        e.target.value = '';
                      }
                    }}
                    className="hidden"
                  />
                  <label
                    htmlFor="wizard-files"
                    className="cursor-pointer flex flex-col items-center justify-center gap-1.5 text-slate-600 dark:text-slate-400 hover:text-cyan-600 dark:hover:text-cyan-400"
                  >
                    <Upload className="w-5 h-5 text-cyan-600 dark:text-cyan-400" />
                    <span className="font-semibold text-xs">
                      Húzd ide a fájlokat vagy kattints a tallózáshoz
                    </span>
                    <span className="text-[10px] text-slate-400">
                      Egyesével vagy egyszerre kijelölve több fájl is hozzáadható
                    </span>
                  </label>
                </div>
              </div>

              {/* Hibaüzenet (Tiltólista, 0-byte, méretkorlát) */}
              {validationError && (
                <div className="p-3 rounded-xl bg-rose-50 dark:bg-rose-950/50 border border-rose-200 dark:border-rose-900/60 text-rose-800 dark:text-rose-300 text-xs flex items-start gap-2 animate-in fade-in">
                  <ShieldAlert className="w-4 h-4 shrink-0 text-rose-600 dark:text-rose-400 mt-0.5" />
                  <div className="whitespace-pre-line leading-relaxed flex-1">
                    {validationError}
                  </div>
                  <button
                    type="button"
                    onClick={() => setValidationError(null)}
                    className="text-rose-500 hover:text-rose-700 font-bold text-xs"
                  >
                    ✕
                  </button>
                </div>
              )}

              {/* Kiválasztott fájlok listája chip-ekben / sorokban */}
              {files.length > 0 && (
                <div className="space-y-1.5 max-h-40 overflow-y-auto pr-1">
                  {files.map((file, idx) => (
                    <div
                      key={`${file.name}-${idx}`}
                      className="flex items-center justify-between p-2 rounded-lg bg-slate-50 dark:bg-slate-800/80 border border-slate-200 dark:border-slate-700 text-xs"
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        {getFileIcon(file.name)}
                        <span className="font-medium text-slate-900 dark:text-white truncate max-w-[280px]">
                          {file.name}
                        </span>
                        <span className="text-[11px] text-slate-400 font-mono shrink-0">
                          ({formatBytes(file.size)})
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleRemoveFile(idx)}
                        className="p-1 text-slate-400 hover:text-rose-600 rounded transition cursor-pointer"
                        title="Fájl eltávolítása"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Szervezeti Tárhely Kvóta Kijelző */}
              <div className="pt-2 border-t border-slate-100 dark:border-slate-800 space-y-1.5">
                <div className="flex items-center justify-between text-[11px] font-semibold text-slate-600 dark:text-slate-400">
                  <span className="flex items-center gap-1.5">
                    <HardDrive className="w-3.5 h-3.5 text-slate-500" />
                    <span>Szervezeti tárhely kvóta:</span>
                  </span>
                  <span>
                    {formatBytes(projectedStorageBytes)} / {maxStorageMb} MB ({Math.min(100, Math.round((projectedStorageBytes / maxStorageBytes) * 100))}%)
                  </span>
                </div>

                {/* Haladási sáv */}
                <div className="w-full h-2 rounded-full bg-slate-200 dark:bg-slate-800 overflow-hidden flex">
                  <div
                    style={{ width: `${Math.min(100, (storageUsedNow / (maxStorageBytes || Infinity)) * 100)}%` }}
                    className="bg-cyan-500 h-full"
                    title={`Eddig felhasznált: ${formatBytes(storageUsedNow)}`}
                  />
                  {totalNewFilesBytes > 0 && (
                    <div
                      style={{ width: `${Math.min(100 - (storageUsedNow / (maxStorageBytes || Infinity)) * 100, (totalNewFilesBytes / (maxStorageBytes || Infinity)) * 100)}%` }}
                      className={`${isOverQuota ? 'bg-rose-500 animate-pulse' : 'bg-emerald-500'} h-full`}
                      title={`Új fájlok: ${formatBytes(totalNewFilesBytes)}`}
                    />
                  )}
                </div>

                {/* Kvótatúllépés Riasztás */}
                {isOverQuota && (
                  <div className="p-2.5 rounded-xl bg-amber-50 dark:bg-amber-950/60 border border-amber-300 dark:border-amber-800 text-amber-900 dark:text-amber-200 text-xs flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 shrink-0 text-amber-600 dark:text-amber-400" />
                    <span className="leading-snug">
                      <strong>Kvótatúllépés!</strong> A kiválasztott fájlokkal a szervezet elérné a tárhelykorlátot ({formatBytes(projectedStorageBytes)} / {maxStorageMb} MB). Távolítson el fájlt a folytatáshoz!
                    </span>
                  </div>
                )}
              </div>
            </div>

            <div className="flex justify-between pt-3 border-t border-slate-100 dark:border-slate-800">
              <button
                type="button"
                onClick={() => setStep(1)}
                className="px-4 py-2 rounded-xl text-xs font-semibold text-slate-600 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white flex items-center gap-1.5 transition cursor-pointer"
              >
                <ArrowLeft className="w-4 h-4" />
                <span>Vissza</span>
              </button>
              <button
                type="button"
                disabled={(mode === 'supplement' ? (!hasContent && files.length === 0) : !title.trim()) || isOverQuota}
                onClick={() => setStep(3)}
                className="px-5 py-2 rounded-xl bg-cyan-600 hover:bg-cyan-500 disabled:opacity-40 text-white font-bold text-xs shadow-md shadow-cyan-600/20 flex items-center gap-2 transition cursor-pointer"
                title={isOverQuota ? 'Kvótatúllépés miatt nem folytatható' : undefined}
              >
                <span>Tovább az ellenőrzéshez</span>
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {/* 3. Lépés: Ellenőrzés és Jóváhagyás */}
        {step === 3 && (
          <div className="space-y-4">
            <div className="p-4 rounded-xl bg-slate-50 dark:bg-slate-950/60 border border-slate-200 dark:border-slate-800 space-y-2.5 text-xs">
              <div className="flex justify-between items-center text-slate-500 dark:text-slate-400 pb-2 border-b border-slate-200/60 dark:border-slate-800">
                <span>Kiválasztott téma:</span>
                <span className="font-bold text-slate-900 dark:text-white flex items-center gap-1.5">
                  <span>{selectedPage?.icon || '📄'}</span>
                  <span>{selectedPage?.title}</span>
                </span>
              </div>
              <div className="flex justify-between items-center text-slate-500 dark:text-slate-400 pb-2 border-b border-slate-200/60 dark:border-slate-800">
                <span>Törvényi pont:</span>
                <span className="font-mono text-cyan-600 dark:text-cyan-400 font-bold">{pont || '–'}</span>
              </div>
              <div className="flex justify-between items-center text-slate-500 dark:text-slate-400 pb-2 border-b border-slate-200/60 dark:border-slate-800">
                <span>Művelet:</span>
                <span className="font-semibold text-slate-900 dark:text-white">
                  {mode === 'supplement' ? 'Meglévő bejegyzés kiegészítése' : mode === 'template' ? 'Új bejegyzés a közzétételi lista alapján' : 'Új bejegyzés'}
                </span>
              </div>
              <div className="flex justify-between items-center gap-3 text-slate-500 dark:text-slate-400 pb-2 border-b border-slate-200/60 dark:border-slate-800">
                <span className="shrink-0">{mode === 'supplement' ? 'Kiegészítendő bejegyzés:' : 'Dokumentum címe:'}</span>
                <span className="font-bold text-slate-900 dark:text-white text-right">{mode === 'supplement' ? target?.cim : title}</span>
              </div>
              <div className="flex justify-between items-start text-slate-500 dark:text-slate-400">
                <span>Csatolt állományok:</span>
                <div className="text-right">
                  {files.length === 0 ? (
                    <span className="text-slate-400 italic">Nincs csatolmány</span>
                  ) : (
                    <div className="space-y-1">
                      <span className="text-emerald-600 dark:text-emerald-400 font-bold">
                        {files.length} db fájl ({formatBytes(totalNewFilesBytes)})
                      </span>
                      <div className="text-[11px] text-slate-600 dark:text-slate-400 font-mono space-y-0.5">
                        {files.map((f, i) => (
                          <div key={i} className="truncate max-w-[220px]">
                            • {f.name} ({formatBytes(f.size)})
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="flex justify-between pt-3 border-t border-slate-100 dark:border-slate-800">
              <button
                type="button"
                onClick={() => setStep(2)}
                className="px-4 py-2 rounded-xl text-xs font-semibold text-slate-600 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white flex items-center gap-1.5 transition cursor-pointer"
              >
                <ArrowLeft className="w-4 h-4" />
                <span>Módosítás</span>
              </button>
              <button
                type="button"
                disabled={isSubmitting || isOverQuota}
                onClick={handleFinish}
                className="px-6 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white font-bold text-xs shadow-md shadow-emerald-600/20 flex items-center gap-2 transition cursor-pointer"
              >
                <CheckCircle2 className="w-4 h-4" />
                <span>{isSubmitting ? 'Közzététel folyamatban...' : 'Azonnali Közzététel'}</span>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
